import axios, { AxiosError, type AxiosRequestConfig } from 'axios';
import type { ApiResponse } from '@/types/api';
import { getBackendUrl } from './backendUrl';
import { getSessionToken } from './session';
import { isErrorCode, type ErrorCode } from './errorCodes';

// Axios codes that mean "the transport never reached a verdict" — no
// response, no server-issued `code`, so `ApiError.transport` is the only
// channel that can carry the reason. `as const` so `Transport` is DERIVED
// from the map's values rather than hand-written as a second parallel
// union — adding a third axios code is then a one-line map entry, not
// another branch. Keyed off the exported `AxiosError.*` constants (not bare
// string literals) so a rename in axios itself is a compile error here.
const TRANSPORT_BY_AXIOS_CODE = {
  [AxiosError.ETIMEDOUT]: 'timeout',
  [AxiosError.ERR_NETWORK]: 'network',
} as const;

export type Transport = (typeof TRANSPORT_BY_AXIOS_CODE)[keyof typeof TRANSPORT_BY_AXIOS_CODE];

// The same table, typed for the way it is actually read: with a string that
// came off the wire. Indexing the `as const` object directly types a MISS as
// `Transport`, which would let the compiler believe every failed request has
// a transport when most (a 404, a 500, a browser abort) have none.
const TRANSPORT_LOOKUP: Readonly<Record<string, Transport | undefined>> = TRANSPORT_BY_AXIOS_CODE;

export class ApiError extends Error {
  // Closed-set wire code (ADR-0006 §5). String union, NOT a number — the
  // retired envelope's numeric `code` is gone. Error display keys off this
  // via `errorCodeMessageKey` → i18n; never off `message`.
  code?: ErrorCode;
  status?: number;
  path?: string;
  method?: string;
  timestamp?: string;
  // Machine-readable detail from the error envelope (`error.extras`): zod
  // issues, field/reason bags, provider error bodies, idempotency markers.
  extras?: Record<string, unknown>;
  // Raw response body as received (the whole `{ error: { ... } }` envelope),
  // kept for callers that still read feature-specific fields off it.
  body?: unknown;
  // Client-side transport failure: the request never produced an HTTP
  // response, so `status` and `code` are BOTH absent and the two channels
  // error display normally uses are empty. Deliberately not folded into
  // `code` — that is the closed-set SERVER code (ADR-0006 §5), and a value
  // the server never sent would make `errorCodeMessageKey` look up a key
  // that cannot exist. Shared with #2288, which keys the retry policy off
  // the same field.
  transport?: Transport;

  constructor(message: string, init?: Partial<ApiError>) {
    super(message);
    this.name = 'ApiError';
    // Explicit per-field assignment (not Object.assign) so:
    //   1. TS catches typos at the call site.
    //   2. Future additions to ApiError show up here as compile
    //      errors until populated, instead of silently missing.
    this.code = init?.code;
    this.status = init?.status;
    this.path = init?.path;
    this.method = init?.method;
    this.timestamp = init?.timestamp;
    this.extras = init?.extras;
    this.body = init?.body;
    this.transport = init?.transport;
  }
}

// Single global value. Lower bound = 2 × 16.6s, the #2151 authGuard retry
// shape (2026-08-21 incident record; retry lives in `platform/auth/guards.ts`
// and is route-agnostic, so no per-endpoint override can cover it) ⇒ 33.2s +
// edge/TLS margin ⇒ 40s. Also above the platform's 15s outbound ceilings.
// Upper bound < 60s so `DISPATCH_RUN_REQUEST_TIMEOUT_MS` stays an override.
// 7-day prod p99 of any fetch route is 6.1s (2026-09-21), so this is sized by
// the failure shape, not the happy path. Per-endpoint override = pass
// `{ timeout }` in the `http.*` config arg (see DispatchDashboard /
// analyticsApi).
export const API_DEFAULT_TIMEOUT_MS = 40_000;

const api = axios.create({
  baseURL: getBackendUrl(),
  timeout: API_DEFAULT_TIMEOUT_MS,
  // Make a timeout say so in its own code (#2289). Unset, the xhr and http
  // adapters report a timeout as ECONNABORTED — the SAME code axios's
  // `xhr.js` uses in `onabort` ("Request aborted"), so no code-only check can
  // tell a slow server from a cancelled request. With this on, every timeout path
  // emits ETIMEDOUT and ECONNABORTED is left to aborts alone. Set on the
  // instance, so the per-endpoint `{ timeout }` overrides keep it —
  // `mergeConfig` has no special case for `transitional` and merges it deeply
  // (verified, not assumed).
  transitional: { clarifyTimeoutError: true },
  headers: {
    'Content-Type': 'application/json',
  },
});

// Public endpoints that legitimately fire before the user has a session
// (e.g. system bootstrap on cold load). The request interceptor
// suppresses its "no session token" warning for these so production
// console output isn't polluted with false-positive auth signals on
// every page load.
const PUBLIC_PATH_PREFIXES = ['/api/public/', '/api/auth/sign-'];

api.interceptors.request.use((config) => {
  const token = getSessionToken();
  if (import.meta.env.DEV) {
    console.log('[API] Request interceptor - token available:', !!token, 'for URL:', config.url);
  }
  if (token) {
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${token}`;
  } else if (import.meta.env.DEV) {
    const url = config.url ?? '';
    const isPublic = PUBLIC_PATH_PREFIXES.some((p) => url.startsWith(p));
    if (!isPublic) {
      console.warn('[API] No session token available for authenticated request');
    }
  }
  return config;
});

/**
 * Event dispatched on 401 responses. `BetterAuthContext` listens for
 * this and performs a React Router navigation, preserving SPA state
 * (no full page reload). Keeping the trigger in axios but the handling
 * in the React tree means tests can intercept the event without a JSDOM
 * `window.location` shim.
 */
// `as const` narrows the literal type so it matches the `WindowEventMap`
// key declared below — `addEventListener` only accepts `keyof WindowEventMap`
// when the literal is preserved.
export const AUTH_UNAUTHORIZED_EVENT = 'auth:unauthorized' as const;

// Augment `WindowEventMap` so listeners receive a properly typed
// `CustomEvent` (not a plain `Event`). The literal must duplicate
// `AUTH_UNAUTHORIZED_EVENT` because TS interface index signatures only
// accept string literal types — there is no way to spell this with the
// const expression. A unit test pins both sides equal so they cannot
// drift (`api.test.ts`).
declare global {
  interface WindowEventMap {
    'auth:unauthorized': CustomEvent<void>;
  }
}

// Response interceptor: surface 401s to the auth layer; never trigger
// navigation directly (would bypass React Router and lose state).
//
// Single-owner state mutation: the BetterAuthContext listener calls
// `applyAuthState(null, null)` → `persistSession(null)` → `storeSession(null)`,
// which clears the persisted session. The interceptor MUST NOT also call
// `localStorage.removeItem` here — duplicate ownership is a maintenance
// trap (one side could change the storage key and the other would silently
// continue to clear the wrong slot).
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      console.error('[API] Authentication failed');
      window.dispatchEvent(new CustomEvent(AUTH_UNAUTHORIZED_EVENT));
    }
    return Promise.reject(error);
  },
);

type Http = {
  get: <T>(url: string, config?: AxiosRequestConfig) => Promise<T>;
  post: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) => Promise<T>;
  put: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) => Promise<T>;
  patch: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) => Promise<T>;
  delete: <T>(url: string, config?: AxiosRequestConfig) => Promise<T>;
};

/**
 * Unwrap the success envelope (ADR-0006 §5): every 2xx JSON body is
 * `{ data: T }`, so return the inner `data`. Error envelopes never reach
 * here — the backend signals failure with a non-2xx status, so axios
 * rejects and `rethrowWithServerMessage` owns them. A body with no `data`
 * key (204 No Content, non-enveloped auth/bootstrap payloads) passes
 * through untouched.
 */
const handleResponse = <T>(body: unknown): T => {
  if (body !== null && typeof body === 'object' && 'data' in body) {
    return (body as { data: T }).data;
  }
  return body as T;
};

/**
 * Re-throw Axios errors as an `ApiError` carrying the structured error
 * envelope (ADR-0006 §5): `{ error: { code, message, extras } }`. The
 * closed-set `code` drives i18n display; the human `message` is a fallback
 * only; `extras` carries machine-readable detail.
 */
function rethrowWithServerMessage(error: unknown, method: string, url: string): never {
  if (error instanceof Error) {
    const response = (error as { response?: { status?: unknown; data?: unknown } }).response;
    const envelope = (
      response?.data as
        | { error?: { code?: unknown; message?: unknown; extras?: unknown } }
        | undefined
    )?.error;
    const serverMessage = envelope?.message;
    const displayMessage = typeof serverMessage === 'string' ? serverMessage : error.message;
    const status = typeof response?.status === 'number' ? response.status : undefined;
    // Timeout, because `clarifyTimeoutError` is set on the instance above:
    // `handleTimeout` in axios's `xhr.js` and `handleRequestTimeout` in its
    // `http.js` both resolve to ETIMEDOUT under that flag, and
    // `helpers/composeSignals.js` emits it unconditionally — those are every
    // timeout site axios has. ECONNABORTED is deliberately NOT in the map:
    // with the flag on, its only remaining emitter is `xhr.js`'s `onabort`,
    // a browser-initiated abort, and telling someone whose
    // request was cancelled that the server was slow is a worse answer than
    // the generic copy this replaces. Network, because ERR_NETWORK (#2316)
    // means the same single thing everywhere axios emits it — the transport
    // never reached a verdict — with no second meaning to disambiguate.
    const transportCode = (error as { code?: unknown }).code;
    // An own-property check, not a bare index: this is a plain object literal, so
    // `TRANSPORT_LOOKUP['constructor']` and friends return an inherited
    // function rather than `undefined`, and `transport` would then be a
    // truthy non-`Transport` that `resolveApiError` would happily branch on.
    // No axios code collides with an `Object.prototype` name today, so this
    // closes a hole rather than fixing a bug — but the old strict compare
    // had no such hole, and a lookup keyed by a wire value should not
    // inherit anything. `Object.hasOwn` would read better; the tsconfig
    // `lib` predates it (TS2550), and widening that for one line is a
    // bigger change than this one.
    const transport =
      typeof transportCode === 'string' &&
      Object.prototype.hasOwnProperty.call(TRANSPORT_LOOKUP, transportCode)
        ? TRANSPORT_LOOKUP[transportCode]
        : undefined;
    const code = isErrorCode(envelope?.code) ? envelope.code : undefined;
    const rawExtras = envelope?.extras;
    const extras =
      rawExtras !== null && typeof rawExtras === 'object'
        ? (rawExtras as Record<string, unknown>)
        : undefined;
    // A PICKED summary, never the error object. That object carries
    // `config`, and the request interceptor above writes the session bearer
    // token into `config.headers.Authorization` (and the request body into
    // `config.data`), so handing it to `console.error` publishes a live
    // credential to everything that reads the console: devtools over
    // someone's shoulder, a screenshot, a shared screen, a browser extension
    // (#2315). Every field below was already computed for the throw, so this
    // is a subtraction. Deliberately NOT a deep copy with `headers` deleted:
    // that patches one path, and the next person to put something sensitive
    // on `config` has no reason to know this line exists.
    console.error(`API ${method} ${url} failed:`, {
      status,
      axiosCode: transportCode,
      // The RAW wire value, not the narrowed `code`. An envelope code outside
      // the closed set is the one case worth reading here -- it means the
      // backend emitted something the client does not know -- and narrowing
      // first would print `undefined` in exactly that case. The narrowed value
      // still goes into the `ApiError`, where the closed set is load-bearing.
      envelopeCode: typeof envelope?.code === 'string' ? envelope.code : undefined,
      transport,
      message: displayMessage,
    });
    throw new ApiError(displayMessage, {
      path: url,
      method,
      timestamp: new Date().toISOString(),
      status,
      code,
      extras,
      transport,
      body: response?.data,
    });
  }
  // A thrown non-Error carries no envelope to summarize and no promise about
  // what it holds, so only its type is named.
  console.error(`API ${method} ${url} failed with a non-Error value of type ${typeof error}`);
  throw error;
}

export const http: Http = {
  get: async <T>(url: string, config?: AxiosRequestConfig) => {
    try {
      const response = await api.get(url, config);
      return handleResponse<T>(response.data);
    } catch (error) {
      throw rethrowWithServerMessage(error, 'GET', url);
    }
  },
  post: async <T>(url: string, data?: unknown, config?: AxiosRequestConfig) => {
    try {
      const response = await api.post(url, data, config);
      return handleResponse<T>(response.data);
    } catch (error) {
      throw rethrowWithServerMessage(error, 'POST', url);
    }
  },
  put: async <T>(url: string, data?: unknown, config?: AxiosRequestConfig) => {
    try {
      const response = await api.put(url, data, config);
      return handleResponse<T>(response.data);
    } catch (error) {
      throw rethrowWithServerMessage(error, 'PUT', url);
    }
  },
  patch: async <T>(url: string, data?: unknown, config?: AxiosRequestConfig) => {
    try {
      const response = await api.patch(url, data, config);
      return handleResponse<T>(response.data);
    } catch (error) {
      throw rethrowWithServerMessage(error, 'PATCH', url);
    }
  },
  delete: async <T>(url: string, config?: AxiosRequestConfig) => {
    try {
      const response = await api.delete(url, config);
      return handleResponse<T>(response.data);
    } catch (error) {
      throw rethrowWithServerMessage(error, 'DELETE', url);
    }
  },
};

// Note: User management is performed via Better Auth Admin plugin on the client.

export const authAPI = {
  initAdmin: (payload: { email: string; password: string; name: string }) =>
    http.post<ApiResponse>('/api/public/system/init', payload),
};

export default api;
